import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { UserContext } from '../../../components/shared/useUser';
import { ControlArea, controlAreasSeed } from '../../../mocks/data/controlAreasData';

const MultiLineField: React.FC<{ label: string; values: string[]; onChange: (newValues: string[]) => void; disabled?: boolean }> = ({ label, values, onChange, disabled }) => (
  <TextField
    label={label}
    value={values.join('\n')}
    onChange={(e) => onChange(e.target.value.split('\n'))}
    fullWidth
    multiline
    minRows={2}
    size="small"
    disabled={disabled}
  />
);

const ControlAreasPage: React.FC = () => {
  const { t } = useTranslation('complianceControls');
  const { hasPermissionTo } = useContext(UserContext);

  // Placeholder permission reuse: control areas don't have a dedicated real permission yet,
  // so this reuses ViewComplianceDashboard/AuditsView until a real permission is added server-side.
  const canView = hasPermissionTo ? hasPermissionTo(['ViewComplianceDashboard']) : true;
  // lazy: demo CRUD permission object; Create/Delete are hidden below regardless of these flags
  // because the 12 control areas are a fixed taxonomy for this demo, not a user-managed list.
  const permissions = {
    canRead: canView,
    canUpdate: hasPermissionTo ? hasPermissionTo(['ViewComplianceDashboard']) : true,
  };

  const [controlAreas, setControlAreas] = useState<ControlArea[]>(controlAreasSeed);
  const [viewTarget, setViewTarget] = useState<ControlArea | null>(null);
  const [editTarget, setEditTarget] = useState<ControlArea | null>(null);
  const [editForm, setEditForm] = useState<ControlArea | null>(null);

  const handleView = (area: ControlArea) => setViewTarget(area);

  const handleOpenEdit = (area: ControlArea) => {
    if (!permissions.canUpdate) return;
    setEditTarget(area);
    setEditForm({ ...area });
  };

  const handleCloseEdit = () => {
    setEditTarget(null);
    setEditForm(null);
  };

  const handleSaveEdit = () => {
    if (!editForm) return;
    setControlAreas((prev) => prev.map((area) => (area.id === editForm.id ? editForm : area)));
    handleCloseEdit();
  };

  if (!permissions.canRead) {
    return (
      <PageArea>
        <PageTitle>{t('controlAreas.title')}</PageTitle>
        <Pill text={t('noPermissionToView')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle subTitle={t('controlAreas.subtitle')}>
            {t('controlAreas.title')}
          </PageTitle>
        </Grid>

        <Grid item
          xs={12}>
          <Typography variant="textSM"
            color="text.secondary">
            {t('controlAreas.fixedTaxonomyNote')}
          </Typography>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('controlAreas.title')}
              <Pill text={`${controlAreas.length} ${t('controlAreas.items')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('name')}</TableCell>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell>{t('controlAreas.requiredChecks')}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {controlAreas.map((area) => (
                    <TableRow key={area.id}
                      hover>
                      <TableCell>{area.name}</TableCell>
                      <TableCell>{area.description}</TableCell>
                      <TableCell>
                        {area.requiredChecks.map((check) => (
                          <Chip key={check}
                            label={check}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('controlAreas.viewDetails') as string}>
                          <IconButton
                            size="small"
                            onClick={() => handleView(area)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {permissions.canUpdate && (
                          <Tooltip title={t('edit') as string}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(area)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {controlAreas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}
                        style={{ textAlign: 'center' }}>
                        {t('controlAreas.noResults')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>

      <Dialog
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {viewTarget && (
          <>
            <DialogTitle>{viewTarget.name}</DialogTitle>
            <DialogContent>
              <Typography variant="textSM"
                sx={{ mb: 3 }}>
                {viewTarget.description}
              </Typography>
              {([
                ['requiredChecks', viewTarget.requiredChecks],
                ['rulesOrThresholds', viewTarget.rulesOrThresholds],
                ['evidenceRequirements', viewTarget.evidenceRequirements],
                ['escalationRules', viewTarget.escalationRules],
                ['linkedTasks', viewTarget.linkedTasks],
                ['linkedAudits', viewTarget.linkedAudits],
                ['linkedActionItems', viewTarget.linkedActionItems],
                ['reportingMetrics', viewTarget.reportingMetrics],
              ] as [string, string[]][]).map(([key, values]) => (
                <Box key={key}
                  sx={{ mb: 2 }}>
                  <Typography variant="textXS"
                    fontWeight={600}
                    sx={{ textTransform: 'uppercase', mb: 0.5 }}>
                    {t(`controlAreas.${key}`)}
                  </Typography>
                  {values.map((value) => (
                    <Chip key={value}
                      label={value}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewTarget(null)}
                variant="secondary"
                size="md">
                {t('close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!editTarget}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        {editForm && (
          <>
            <DialogTitle>{t('controlAreas.editControlArea')}: {editForm.name}</DialogTitle>
            <DialogContent>
              <Grid container
                spacing={3}
                sx={{ mt: 0.5 }}>
                <Grid item
                  xs={12}>
                  <MultiLineField
                    label={t('controlAreas.rulesOrThresholds')}
                    values={editForm.rulesOrThresholds}
                    onChange={(values) => setEditForm((prev) => (prev ? { ...prev, rulesOrThresholds: values } : prev))}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <MultiLineField
                    label={t('controlAreas.evidenceRequirements')}
                    values={editForm.evidenceRequirements}
                    onChange={(values) => setEditForm((prev) => (prev ? { ...prev, evidenceRequirements: values } : prev))}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <MultiLineField
                    label={t('controlAreas.escalationRules')}
                    values={editForm.escalationRules}
                    onChange={(values) => setEditForm((prev) => (prev ? { ...prev, escalationRules: values } : prev))}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}
                variant="secondary"
                size="md">
                {t('cancel')}
              </Button>
              <Button onClick={handleSaveEdit}
                variant="primary"
                size="md">
                {t('save')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PageArea>
  );
};

export default ControlAreasPage;
